import { useState, useCallback } from 'react';

interface UseAdminAPIState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

interface UseAdminAPIReturn<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    execute: (...args: any[]) => Promise<T | null>;
}

/**
 * 관리자 API 호출을 위한 범용 커스텀 훅
 *
 * @param apiFunction - 실행할 API 함수
 * @param autoFetch - 컴포넌트 마운트 시 자동으로 데이터를 가져올지 여부 (기본값: true)
 * @returns data, loading, error, refetch, execute
 */
export const useAdminAPI = <T = any>(
    apiFunction: (...args: any[]) => Promise<any>,
    autoFetch: boolean = true
): UseAdminAPIReturn<T> => {
    const [state, setState] = useState<UseAdminAPIState<T>>({
        data: null,
        loading: autoFetch,
        error: null,
    });

    const execute = useCallback(async (...args: any[]): Promise<T | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await apiFunction(...args);

            // Check: request failed
            if (result === false || result === "false") {
                setState({
                    data: null,
                    loading: false,
                    error: 'API 호출에 실패했습니다.',
                });
                return null;
            }

            setState({
                data: result,
                loading: false,
                error: null,
            });
            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
            setState({
                data: null,
                loading: false,
                error: errorMessage,
            });
            return null;
        }
    }, [apiFunction]);

    const refetch = useCallback(async () => {
        await execute();
    }, [execute]);

    return {
        data: state.data,
        loading: state.loading,
        error: state.error,
        refetch,
        execute,
    };
};
